import React from 'react';
import { SECTIONS, fieldMap, PREDEFINED_SECTIONS } from '../../constants/plansConstants';

const SectionsNavigation = ({ 
  activeSection, 
  activePlan,
  aiLoading,
  allSectionsComplete,
  usageLimits,
  onSectionChange, 
  onGenerateActionPlan 
}) => {
  const canGenerateActionPlan = () => {
    if (!usageLimits) return true;
    return usageLimits.action_plans?.remaining > 0;
  };

  const getButtonText = () => {
    if (aiLoading) return 'Generando...';
    if (activePlan?.action_plan_md) {
      return canGenerateActionPlan() ? 'Generar nuevo plan de acción' : 'Regenerar (límite alcanzado)';
    }
    if (!allSectionsComplete) return 'Completa todas las secciones';
    if (!canGenerateActionPlan()) return 'Límite diario alcanzado';
    return 'Generar plan de acción';
  };

  const isButtonDisabled = () => {
    if (!activePlan || aiLoading) return true;
    // Si ya existe un plan de acción, se puede regenerar solo si no se alcanzó el límite
    if (activePlan.action_plan_md) return !canGenerateActionPlan();
    // Si no existe, necesita todas las secciones completas y no haber alcanzado el límite
    return !allSectionsComplete || !canGenerateActionPlan();
  };

  const isSectionComplete = (section) => {
    if (!activePlan) return false;
    const field = fieldMap[section];
    const value = activePlan[field];
    return value && value.trim().length > 0;
  };

  const getCompletedCount = () => {
    if (!activePlan) return 0;
    return SECTIONS.filter(s => isSectionComplete(s)).length;
  };

  const completedCount = getCompletedCount();
  const progress = Math.round((completedCount / SECTIONS.length) * 100);

  return (
    <nav style={{ minWidth: 260 }}>
      <ul className="section-list">
        {SECTIONS.map(s => {
          const isComplete = isSectionComplete(s);
          const isActive = s === activeSection;
          const isPredefined = PREDEFINED_SECTIONS.has(s);
          
          return (
            <li key={s}>
              <button 
                className={`section-btn ${isComplete ? 'complete' : 'incomplete'}`}
                onClick={() => onSectionChange(s)} 
                style={{ fontWeight: isActive ? 'bold' : 'normal' }}
                title={isPredefined ? `${s} (recomendaciones BMC)` : `${s} (sugerencias IA)`}
              >
                <span className="section-status-icon">
                  {isComplete ? '✅' : '⭕'}
                </span>
                <span className="section-name">{s}</span>
                {!isPredefined && (
                  <span
                    className="section-ai-badge"
                    aria-label="Usa IA"
                    title="Este bloque usa inteligencia artificial"
                    style={{
                      marginLeft: 'auto',
                      fontSize: '0.6rem',
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      padding: '1px 5px',
                      borderRadius: 4,
                      background: 'var(--color-primary, #4f46e5)',
                      color: '#fff',
                      lineHeight: 1.4,
                      flexShrink: 0,
                    }}
                  >
                    IA
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      
      {!activePlan?.action_plan_md && (
        <div className="progress-indicator">
          <div className="progress-bar-container">
            <div 
              className={`progress-bar ${progress === 100 ? 'complete' : progress >= 50 ? 'half' : 'start'}`}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <span className="progress-text">{completedCount}/{SECTIONS.length} secciones completas</span>
        </div>
      )}
      
      {!canGenerateActionPlan() && (
        <div className="limit-warning small">
          <span className="limit-icon">⏰</span>
          {activePlan?.action_plan_md 
            ? 'No puedes regenerar hoy. Límite alcanzado.' 
            : 'Límite diario alcanzado. Vuelve mañana.'}
        </div>
      )}
      
      <button 
        onClick={onGenerateActionPlan} 
        className={`primary-btn ${activePlan?.action_plan_md && canGenerateActionPlan() ? 'regenerate-mode' : ''}`}
        disabled={isButtonDisabled()}
        title={
          !allSectionsComplete ? 'Completa todas las secciones primero' : 
          !canGenerateActionPlan() ? 'Has alcanzado el límite diario de generaciones' :
          (activePlan?.action_plan_md ? 'Regenerar el plan de acción con la información actualizada' : '')
        }
      >
        {getButtonText()}
      </button>
    </nav>
  );
};

export default SectionsNavigation;
